import { FC, useContext, useEffect, useState } from 'react';
import useRunTriggers, { Trigger } from '../hooks/useRunTriggers';
import styled, { css } from 'styled-components';
import { getContentType } from '../common/Common';
import { getTranslation } from '../common/Translation';
import { AppContext } from '../contexts/AppContext';
import MediaElement from './MediaElement';
import Link from './Link';
import useFetchDocument from '../hooks/useFetchDocument';

interface ListItemProps {
  documentId: number,
  title?: string,
  subtitle?: string,
  image?: any,
  cellSize?: any,
  textPosition?: string,
  titleColor?: string,
  subtitleColor?: string,
  titleSize?: string,
  subtitleSize?: string,
  textBackground?: string,
  cellBackgroundColor?: string,
  cellFilterColor?: string,
  transparent?: boolean,
  notClickable?: boolean,
  useTriggers?: boolean,
  triggers?: Trigger[],
}

const ListElement = styled.li<{ $rows?: number, $columns?: number, $cellFilterColor?: string, $transparent?: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  overflow: hidden;
  /* box-shadow: ${({ theme }) => theme.elementShadow}; */
  grid-column-end: span ${({ $columns = 1 }) => $columns};
  grid-row-end: span ${({ $rows = 1 }) => $rows};
  opacity: ${({ $transparent }) => $transparent ? 0 : 1};

  & a {
    width: 100%;
    height: 100%;
    display: block;
  }

  ${({ $cellFilterColor }) => $cellFilterColor && css`
    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: ${$cellFilterColor};
      mix-blend-mode: multiply;
      pointer-events: none;
    }
  `}
`;

const ListTitleWrapper = styled.div`
  position: absolute;
  z-index: 2;
  left: 0;
  bottom: 0;
  padding: 20px 30px;
  display: inline-table;
  width: 100%;
  word-break: break-word;

  &.top-left {
    top: 0;
    left: 0;
  }

  &.top-center {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
  }

  &.top-right {
    top: 0;
    left: unset;
    right: 0;
    text-align: right;
  }

  &.mid-left {
    top: 50%;
    transform: translateY(-50%);
    left: 0;
  }

  &.mid-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }

  &.mid-right {
    top: 50%;
    transform: translateY(-50%);
    left: unset;
    right: 0;
    text-align: right;
  }

  &.bottom-left {
    bottom: 0;
    left: 0;
  }

  &.bottom-center {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
  }

  &.bottom-right {
    bottom: 0;
    left: unset;
    right: 0;
    text-align: right;
  }

  &.bg-shadow span {
    text-shadow: 0 1px 2px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.6), 0 0 12px rgba(0,0,0,0.4);
  }

  &.bg-block span {
    background-color: rgba(0,0,0,.4);
    padding: 5px 10px;
    line-height: 1.7em;
    display: inline;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  &.bg-gradient {
  }

  &.bg-hidden {
    display: none;
  }

  &.bg-outline span {
    text-shadow: -2px 0 0 #000, 2px 0 0 #000, 0 -2px 0 #000, 0 2px 0 #000, 0  2px 6px rgba(0,0,0,0.6);
  }
`

const ListTitle = styled.span<{ $size?: string, $color?: string }>`
  display: block;
  font-size: ${({ $size = "1.5em" }) => $size};
  color: ${({ $color = "#ffffff" }) => $color};
  font-weight: 600;
  text-shadow: 1px 1px 1px rgb(0 0 0 / 20%);

  &:empty {
    display: none;
  }
`;

const ListSubTitle = styled.span<{ $size?: string, $color?: string }>`
  display: block;
  font-size: ${({ $size = "1em" }) => $size};
  color: ${({ $color = "#ffffff" }) => $color};
  text-shadow: 1px 1px 1px rgb(0 0 0 / 20%);
`;

const ListImage = styled(MediaElement)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/**
 * Render an item for the list view
 * @returns {JSX.Element} Component template
 */
const ListItem: FC<ListItemProps> = ({documentId, image, title, subtitle, cellSize, textPosition, titleColor, subtitleColor, titleSize, subtitleSize, textBackground, cellBackgroundColor, cellFilterColor, transparent, notClickable, useTriggers, triggers}) => {
  const runTriggers = useRunTriggers();
  const { currentLanguage } = useContext(AppContext);
  const [itemTitle, setItemTitle] = useState<any>(null);
  const [itemSubtitle, setItemSubtitle] = useState<any>(null);
  const [itemMedia, setItemMedia] = useState<any>(null);
  const [contentType, setContentType] = useState<string>("");
  const [mappedCellSize, setMappedCellsize] = useState<any>(null);
  const [data] = useFetchDocument(documentId);

  // Should custom image or document main image be used?
  useEffect(() => {
    const imageData = image?.mediaId ? image : (data?.content?.general?.image || data?.content?.background?.image)
    setItemMedia(imageData);
  }, [image, data])

  // Get content type of a presentation or article
  useEffect(() => {
    setContentType(getContentType(data))
  }, [data]);

  // Get title and subtitle
  useEffect(() => {
    if (data) {
      setItemTitle(getTranslation(title, currentLanguage)?.length > 0 ? getTranslation(title, currentLanguage) : getTranslation(data?.title, currentLanguage));
      setItemSubtitle(getTranslation(subtitle, currentLanguage)?.length > 0 ? getTranslation(subtitle, currentLanguage) : null);
    }
  }, [title, subtitle, currentLanguage, data]);

  // Get the item cellsize. Can be received as a string ("2x3") or an object ({columns: 2, rows: 3})
  useEffect(() => {
    if (typeof cellSize === "string") {
      setMappedCellsize({
        columns: Number(cellSize?.split("x")?.[0] || 1),
        rows: Number(cellSize?.split("x")?.[1] || 1)
      });
    } else {
      // The cellsize values were switched in the old version.
      setMappedCellsize({
        columns: Number(cellSize?.rows || 1),
        rows: Number(cellSize?.columns || 1)
      });
    }
  }, [cellSize]);


  // Do not display link if not available in current language
  if (data?.status && !(data?.title?.[currentLanguage || "no"] || data?.content?.content?.blocks?.[currentLanguage || "no"])) { return null; }

  // Only display if published
  if (data?.status && !data?.status?.includes("published")) { return null; }

  return (
    <ListElement $rows={mappedCellSize?.rows || 1} $columns={mappedCellSize?.columns || 1} $cellFilterColor={cellFilterColor} $transparent={Boolean(transparent)} onClick={() => runTriggers(useTriggers, triggers)}>
      <Link id={documentId} presentationType={contentType} notClickable={Boolean(notClickable)}>
        <>
          {(!(itemMedia?.filters?.brightness === 0)) && <ListImage media={contentType && itemMedia} backgroundColor={cellBackgroundColor}/>}
          <ListTitleWrapper className={`${textPosition} bg-${textBackground}`}>
            {itemSubtitle && (<ListSubTitle $size={subtitleSize} $color={subtitleColor}>{itemSubtitle}</ListSubTitle>)}
            {itemTitle && (<ListTitle $size={titleSize} $color={titleColor}>{itemTitle}</ListTitle>)}
          </ListTitleWrapper>
        </>
      </Link>
    </ListElement>
  );
}

export default ListItem;
